import { useEffect, useRef } from 'react'
import {
  type FieldPath,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
  useForm as useReactHookForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

/**
 * Validation messages are translated when the validation runs, so errors that
 * are already on screen would keep the previous language after a language
 * switch. Re-running the validation resolves them again, scoped to the fields
 * that already report an error so that an untouched field does not surface one
 * ahead of time.
 */
export const useForm = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  props?: UseFormProps<TFieldValues, TContext, TTransformedValues>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> => {
  const form = useReactHookForm(props)
  const {
    i18n: { language },
  } = useTranslation()

  const formRef = useRef(form)
  formRef.current = form

  // biome-ignore lint/correctness/useExhaustiveDependencies: tracked for follow-up cleanup
  useEffect(() => {
    const { formState, trigger } = formRef.current
    const fieldsWithErrors = Object.keys(
      formState.errors,
    ) as FieldPath<TFieldValues>[]

    if (fieldsWithErrors.length > 0) {
      void trigger(fieldsWithErrors)
    }
  }, [language])

  return form
}
